import { FC, useMemo } from 'react'
import { IFaqArticle, IFaqCategory } from 'screens/Admin/KnowleageBase/faq.types'
import useFaqState from 'screens/Admin/KnowleageBase/hooks/useFaqState'
import Tree from 'shared/Tree'
import useExpandAll from 'shared/Tree/ctrl/useExpandAll'
import useTreeExpandCtrl from 'shared/Tree/ctrl/useTreeExpandCtrl'
import useTreeLeafSelectCtrl from 'shared/Tree/ctrl/useTreeLeafSelectCtrl'
import { ITreeGroup, ITreeLeaf } from 'shared/Tree/tree.types'
import css from './knowleageBaseMenu.module.css'
import cn from 'utils/cn'
import KnowleageBaseFilters from '../KnowleageBaseFilters'
import { useRecoilValue } from 'recoil'
import knowleageBaseFiltersAtom from 'screens/KnowleageBase/knowleageBaseFilters.atom'

interface IProps {
  treeSelectCtrl: ReturnType<typeof useTreeLeafSelectCtrl>
}

const KnowleageBaseMenu: FC<IProps> = (props) => {
  const { categories, articles, categoriesMap } = useFaqState()

  const filters = useRecoilValue(knowleageBaseFiltersAtom)

  const filteredArticles = useMemo(() => {
    if(filters.checkpointIds.length === 0) {
      return articles
    }
    return articles.filter((article) =>
      article.relatedCheckpoints.some((checkpoint) => filters.checkpointIds.includes(checkpoint))
    )
  }, [articles, filters.checkpointIds])

  const filteredCategories = useMemo(() => {
    const filteredCategoriedMap = new Map<IFaqCategory['id'], IFaqCategory>()
    filteredArticles.forEach((article) => {
      const category = categoriesMap[article.categoryId]
      if (category) {
        filteredCategoriedMap.set(category.id, category)
      }
    })
    return Array.from(filteredCategoriedMap.values())
  }, [categories, filteredArticles])

  const treeGroups = useMemo(() => {
    return filteredCategories.map((c) => ({ id: c.id, parentId: c.parentId, data: c }))
  }, [filteredCategories])

  const treeLeafs = useMemo(() => {
    return filteredArticles
      .map((article) => ({ key: article.id, data: article, groupId: article.categoryId }))
      .sort((a, b) => a.data.ordering - b.data.ordering)
  }, [filteredArticles])

  const expandAllCtrl = useExpandAll()
  const treeExpandCtrl = useTreeExpandCtrl(
    'etb/student/faq',
    expandAllCtrl.isOpenAll,
    treeGroups,
    treeLeafs,
    expandAllCtrl.setIsOpenAll,
    expandAllCtrl.clickedOnExpandAll,
    expandAllCtrl.setClickedOnExpandAll
  )

  const renderArticle = (leaf: ITreeLeaf<IFaqArticle, IFaqCategory['id']>) => {
    return (
      <div
        className='tree-leaf text--secondary text--body-m'
        style={{ width: '100%' }}
        data-selected={leaf.data.id === props.treeSelectCtrl.selectedItem}
      >
        <div className='line-clamp'>
          <p title={leaf.data.title}>{leaf.data.title}</p>
        </div>
      </div>
    )
  }

  const renderCategory = (group: ITreeGroup<IFaqCategory, IFaqCategory['id']>, isOpen?: boolean) => {
    return (
      <div data-open={isOpen ?? false} className={cn(css.category, 'text--body-m line-clamp')}>
        {group.data.title}
      </div>
    )
  }
  return (
    <div className={css.wrapper}>
      <KnowleageBaseFilters expandCtrl={treeExpandCtrl} leafSelectCtrl={props.treeSelectCtrl}/>
      <Tree
        leafs={treeLeafs}
        groups={treeGroups}
        renderLeaf={renderArticle}
        renderGroup={renderCategory}
        expandCtrl={treeExpandCtrl}
        leafSelectCtrl={props.treeSelectCtrl}
      />
    </div>
  )
}

export default KnowleageBaseMenu
